import { Button, makeStyles } from "@material-ui/core";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useToast } from "../../context/ToastContext";

import React, { useRef, useState } from "react";
import Select from "react-select";
import { number, object, string } from "yup";
import { Endpoint } from "../../api/base";
import { states } from "../../components/shared/helpers/states";
import FMUInputField from "../../components/shared/FormIkMUFormComponent/FMUInputField";
import ContactsDialog from "./ContactsDialog";
import { generateQuickGuid } from "../../components/shared/helpers/helper";
import ContactsIcon from "@material-ui/icons/Contacts";
import { PrimaryButton } from "../../components/shared/helpers/GlobalStylesv5";
import { reactSelectStyles } from "../../components/NewCaseView/reactSelectStyles";
import { tokens } from "../../theme/tokens";

const useStyle = makeStyles(() => ({
  card: {
    maxWidth: "940px",
    margin: "0 auto",
    backgroundColor: tokens.color.surface.default,
    border: `1px solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.md,
    padding: tokens.space[6],
    boxSizing: "border-box",
  },
  section: {
    marginBottom: tokens.space[6],
  },
  sectionLabel: {
    margin: `0px 0px ${tokens.space[4]} 0px`,
    paddingBottom: tokens.space[2],
    borderBottom: `1px solid ${tokens.color.border.default}`,
    fontSize: tokens.type.dense.size,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: tokens.color.text.secondary,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: `${tokens.space[4]} ${tokens.space[5]}`,
    "@media (max-width: 720px)": { gridTemplateColumns: "1fr" },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[1],
    minWidth: "0px",
  },
  fieldWide: {
    gridColumn: "1 / -1",
  },
  fieldLabel: {
    fontSize: tokens.type.dense.size,
    fontWeight: 500,
    color: tokens.color.text.secondary,
  },
  req: {
    color: tokens.color.status.error,
    marginLeft: "2px",
  },
  input: {
    width: "100%",
    height: "40px",
    backgroundColor: tokens.color.surface.default,
    border: `1px solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.sm,
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": { borderColor: tokens.color.border.strong },
    "&.Mui-focused": { borderColor: tokens.color.focus.ring },
    "& .MuiInputBase-input": {
      padding: `0px ${tokens.space[3]}`,
      fontSize: tokens.type.dense.size,
      color: tokens.color.text.primary,
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: tokens.color.text.disabled,
    },
  },
  error: {
    fontSize: tokens.type.caption.size,
    color: tokens.color.status.error,
  },
  contactsButton: {
    alignSelf: "flex-start",
    textTransform: "none",
    fontWeight: 500,
    border: `1px solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.sm,
    color: tokens.color.text.primary,
    padding: `${tokens.space[2]} ${tokens.space[4]}`,
    "&:hover": {
      borderColor: tokens.color.border.strong,
      backgroundColor: tokens.color.surface.hover,
    },
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: tokens.space[6],
    paddingTop: tokens.space[5],
    borderTop: `1px solid ${tokens.color.border.default}`,
  },
}));

function AddClient({ initState }) {
  const classes = useStyle();
  const { showToast } = useToast();
  const [dialog, setDialog] = useState(false);
  const formRef = useRef();
  const isEdit = initState.client_id !== "";

  // A plain helper (not a component) so React keeps the inputs mounted across
  // renders — an inner component type would remount and drop focus.
  const renderField = ({ label, name, type, disabled, required, dollar, wide }) => (
    <div
      key={name}
      className={`${classes.field} ${wide ? classes.fieldWide : ""}`}
    >
      <label className={classes.fieldLabel} htmlFor={name}>
        {label}
        {required && <span className={classes.req}>*</span>}
      </label>
      <Field
        id={name}
        name={name}
        type={type}
        disabled={disabled}
        dollar={dollar}
        component={FMUInputField}
        className={classes.input}
        disableUnderline
      />
      <ErrorMessage name={name}>
        {(msg) => <div className={classes.error}>{msg}</div>}
      </ErrorMessage>
    </div>
  );

  const handleSubmit = (v) => {
    let obj;
    if (v.client_id === "") {
      obj = {
        client_id: generateQuickGuid("client"),
        client_name: v.client_name,
        street_address: v.street_address_line2
          ? `${v.street_address} ${v.street_address_line2}`
          : `${v.street_address}`,
        city: v.city,
        zip_code: v.zip_code,
        abbreviation: v.abbreviation,
        state: v.state,
        hourly_rate: v.hourly_rate,
        contacts: v.contacts,
        database_search_rate: v.database_search_rate,
        local_investigator_rate: v.local_investigator_rate,
        local_investigator_mileage_rate: v.local_investigator_mileage_rate,
      };
    } else {
      obj = {
        client_id: v.client_id,
        client_name: v.client_name,
        street_address: v.street_address_line2
          ? `${v.street_address} ${v.street_address_line2}`
          : `${v.street_address}`,
        city: v.city,
        zip_code: v.zip_code,
        abbreviation: v.abbreviation,
        hourly_rate: v.hourly_rate,
        database_search_rate: v.database_search_rate,
        local_investigator_rate: v.local_investigator_rate,
        contacts: v.contacts,
        state: v.state,
        local_investigator_mileage_rate: v.local_investigator_mileage_rate,
      };
    }
    (v.client_id === "" ? Endpoint.createClient : Endpoint.updateClient)(obj)
      .then((res) => {
        showToast("Client added successfully.", "success");
        window.location.reload();
      })
      .catch((err) => {
        showToast(
          err?.response?.data?.error ||
            "Sorry something went wrong, please try again later.",
          "error"
        );
      });
  };

  const validateSchema = object().shape({
    client_name: string().required("Client name is required"),
    street_address: string().required("Street address is required"),
    city: string().required("City  is required"),
    zip_code: string().required("Zipcode is required"),
    abbreviation: string().required("Abbreviation is required"),
    state: string().required("State is required"),
    hourly_rate: number().moreThan(0),
    database_search_rate: number().moreThan(0),
    local_investigator_rate: number().moreThan(0),
    local_investigator_mileage_rate: number().moreThan(0),
  });

  return (
    <div className={classes.card}>
      <Formik
        initialValues={initState}
        validationSchema={validateSchema}
        validateOnBlur={false}
        innerRef={formRef}
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        render={({ values, setFieldValue }) => (
          <Form>
            <div className={classes.section}>
              <p className={classes.sectionLabel}>Client details</p>
              <div className={classes.grid}>
                {renderField({
                  label: "Client name",
                  name: "client_name",
                  type: "text",
                  required: true,
                  disabled: isEdit,
                })}
                {renderField({
                  label: "Abbreviation",
                  name: "abbreviation",
                  type: "text",
                  required: true,
                  disabled: isEdit,
                })}
                {renderField({
                  label: "Street address",
                  name: "street_address",
                  type: "text",
                  required: true,
                  wide: true,
                })}
                {renderField({
                  label: "Street address line 2",
                  name: "street_address_line2",
                  type: "text",
                  wide: true,
                })}
                {renderField({
                  label: "City",
                  name: "city",
                  type: "text",
                  required: true,
                })}
                <div className={classes.field}>
                  <label className={classes.fieldLabel} htmlFor="state">
                    State<span className={classes.req}>*</span>
                  </label>
                  <Select
                    inputId="state"
                    value={values.state ? { label: values.state, value: values.state } : null}
                    onChange={(e) => {
                      setFieldValue("state", e.value, false);
                    }}
                    options={states}
                    styles={reactSelectStyles}
                    placeholder="Select…"
                  />
                  <ErrorMessage name={`state`}>
                    {(msg) => <div className={classes.error}>{msg}</div>}
                  </ErrorMessage>
                </div>
                {renderField({
                  label: "Zip code",
                  name: "zip_code",
                  type: "text",
                  required: true,
                })}
              </div>
            </div>

            <div className={classes.section}>
              <p className={classes.sectionLabel}>Billing rates</p>
              <div className={classes.grid}>
                {renderField({
                  label: "Hourly rate",
                  name: "hourly_rate",
                  type: "number",
                  required: true,
                  dollar: true,
                })}
                {renderField({
                  label: "Database search rate",
                  name: "database_search_rate",
                  type: "number",
                  required: true,
                  dollar: true,
                })}
                {renderField({
                  label: "Local investigator rate",
                  name: "local_investigator_rate",
                  type: "number",
                  required: true,
                  dollar: true,
                })}
                {renderField({
                  label: "Local investigator mileage rate",
                  name: "local_investigator_mileage_rate",
                  type: "number",
                  required: true,
                  dollar: true,
                })}
              </div>
            </div>

            <div className={classes.section}>
              <p className={classes.sectionLabel}>Contacts</p>
              <Button
                className={classes.contactsButton}
                startIcon={<ContactsIcon />}
                onClick={() => {
                  setDialog(true);
                }}
              >
                Manage contacts ({values.contacts.length})
              </Button>
            </div>

            <div className={classes.footer}>
              <PrimaryButton type={"submit"}>
                {isEdit ? "Update Client" : "Add Client"}
              </PrimaryButton>
            </div>

            <ContactsDialog
              dialog={dialog}
              handleCloseDialog={(e) => {
                setDialog(false);
              }}
              values={values}
              setFieldValue={setFieldValue}
            />
          </Form>
        )}
      />
    </div>
  );
}

export default AddClient;
